/**
 * GameObject nodes — find, create, destroy entities.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

export const GetSelf: NodeBehaviour = {
  id: 'gameobject.self',
  name: 'Self',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'User',
  description: 'Returns the entity this script is attached to.',
  keywords: ['this', 'me', 'owner', 'entity'],
  inputs: [],
  outputs: [
    output('entity', 'Entity', 'entity'),
  ],
  execute(ctx) {
    ctx.setOutput('entity', ctx.entityId);
  },
  compile(ctx) {
    return ctx.emit('event_param:entityId', [], 'entity');
  },
};

export const FindByName: NodeBehaviour = {
  id: 'gameobject.findByName',
  name: 'Find By Name',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Search',
  description: 'Finds an entity by name.',
  keywords: ['search', 'lookup', 'get'],
  inputs: [
    input('name', 'Name', 'string', ''),
  ],
  outputs: [
    output('entity', 'Entity', 'entity'),
    output('found', 'Found', 'bool'),
  ],
  execute(ctx) {
    const name = ctx.getInput<string>('name') ?? '';
    const entity = ctx.bridge.call('findByName', name) as number;
    ctx.setOutput('entity', entity);
    ctx.setOutput('found', entity !== -1);
  },
  compile(ctx) {
    return ctx.emit('bridge:findByName', [ctx.resolveInput('name')], 'entity');
  },
};

export const FindByTag: NodeBehaviour = {
  id: 'gameobject.findByTag',
  name: 'Find By Tag',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Tag',
  description: 'Finds entities with a given tag.',
  keywords: ['search', 'filter', 'group'],
  inputs: [
    input('tag', 'Tag', 'string', ''),
  ],
  outputs: [
    output('entity', 'First Entity', 'entity'),
    output('count', 'Count', 'i32'),
  ],
  execute(ctx) {
    const tag = ctx.getInput<string>('tag') ?? '';
    const result = ctx.bridge.call('findByTag', tag) as { entity: number; count: number };
    ctx.setOutput('entity', result?.entity ?? -1);
    ctx.setOutput('count', result?.count ?? 0);
  },
  compile(ctx) {
    return ctx.emit('bridge:findByTag', [ctx.resolveInput('tag')], 'entity');
  },
};

export const DestroyEntity: NodeBehaviour = {
  id: 'gameobject.destroy',
  name: 'Destroy Entity',
  category: 'Events',
  color: '#e74c3c',
  icon: 'Trash2',
  description: 'Destroys an entity.',
  keywords: ['remove', 'delete', 'kill'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const entity = ctx.getInput<number>('entity') ?? ctx.entityId;
    ctx.bridge.call('destroyEntity', entity);
  },
  compile(ctx) {
    ctx.emit('bridge:destroyEntity', [ctx.resolveInput('entity')], 'entity', true);
  },
};

export const SetActive: NodeBehaviour = {
  id: 'gameobject.setActive',
  name: 'Set Active',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Eye',
  description: 'Enables or disables an entity.',
  keywords: ['enable', 'disable', 'visible', 'hide', 'show'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('active', 'Active', 'bool', true),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    const entity = ctx.getInput<number>('entity') ?? ctx.entityId;
    const active = ctx.getInput<boolean>('active') ?? true;
    ctx.bridge.call('setActive', entity, active);
  },
  compile(ctx) {
    ctx.emit('bridge:setActive', [ctx.resolveInput('entity'), ctx.resolveInput('active')], 'bool', true);
  },
};

export const IsActive: NodeBehaviour = {
  id: 'gameobject.isActive',
  name: 'Is Active',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Eye',
  description: 'Checks if an entity is active.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('active', 'Active', 'bool'),
  ],
  execute(ctx) {
    const entity = ctx.getInput<number>('entity') ?? ctx.entityId;
    const active = ctx.bridge.call('isActive', entity);
    ctx.setOutput('active', active as boolean);
  },
  compile(ctx) {
    return ctx.emit('bridge:isActive', [ctx.resolveInput('entity')], 'bool');
  },
};

export const GetEntityName: NodeBehaviour = {
  id: 'gameobject.getName',
  name: 'Get Name',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Type',
  description: 'Gets the name of an entity.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('name', 'Name', 'string'),
  ],
  execute(ctx) {
    const entity = ctx.getInput<number>('entity') ?? ctx.entityId;
    const name = ctx.bridge.call('getEntityName', entity);
    ctx.setOutput('name', name as string);
  },
  compile(ctx) {
    return ctx.emit('bridge:getEntityName', [ctx.resolveInput('entity')], 'string');
  },
};

export const Instantiate: NodeBehaviour = {
  id: 'gameobject.instantiate',
  name: 'Clone Entity',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Copy',
  description: 'Clones an existing entity (with all components) at the given position.',
  keywords: ['spawn', 'create', 'clone', 'instance', 'duplicate'],
  inputs: [
    flowIn(),
    input('source', 'Source', 'gameobject', -1),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('rotation', 'Rotation', 'f32', 0),
  ],
  outputs: [
    flowOut(),
    output('entity', 'Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:cloneEntity', [
      ctx.resolveInput('source'),
      ctx.resolveInput('x'),
      ctx.resolveInput('y'),
      ctx.resolveInput('rotation'),
    ], 'entity', true);
  },
  execute(ctx) {
    const eid = ctx.bridge.call('cloneEntity',
      ctx.getInput<number>('source'),
      ctx.getInput<number>('x'),
      ctx.getInput<number>('y'),
      ctx.getInput<number>('rotation'),
    ) as number ?? -1;
    ctx.setOutput('entity', eid);
  },
};

export const InstantiateAt: NodeBehaviour = {
  id: 'gameobject.instantiateAt',
  name: 'Clone At Entity',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Copy',
  description: 'Clones an entity at another entity\'s position with optional offset.',
  keywords: ['spawn', 'create', 'clone', 'instance', 'duplicate'],
  inputs: [
    flowIn(),
    input('source', 'Source', 'gameobject', -1),
    input('target', 'Target Entity', 'entity', 0),
    input('offsetX', 'Offset X', 'f32', 0),
    input('offsetY', 'Offset Y', 'f32', 0),
  ],
  outputs: [
    flowOut(),
    output('entity', 'Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:cloneEntityAt', [
      ctx.resolveInput('source'),
      ctx.resolveInput('target'),
      ctx.resolveInput('offsetX'),
      ctx.resolveInput('offsetY'),
    ], 'entity', true);
  },
  execute(ctx) {
    const eid = ctx.bridge.call('cloneEntityAt',
      ctx.getInput<number>('source'),
      ctx.getInput<number>('target'),
      ctx.getInput<number>('offsetX'),
      ctx.getInput<number>('offsetY'),
    ) as number ?? -1;
    ctx.setOutput('entity', eid);
  },
};

export const GetEntityTag: NodeBehaviour = {
  id: 'gameobject.getTag',
  name: 'Get Tag',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Tag',
  description: 'Gets the tag of an entity.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('tag', 'Tag', 'string'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getEntityTag', [ctx.resolveInput('entity')], 'string');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('tag', ctx.bridge.call('bridge:getEntityTag', eid) as string ?? '');
  },
};

export const SetEntityTag: NodeBehaviour = {
  id: 'gameobject.setTag',
  name: 'Set Tag',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Tag',
  description: 'Sets the tag of an entity.',
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('tag', 'Tag', 'string', ''),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setEntityTag', [ctx.resolveInput('entity'), ctx.resolveInput('tag')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setEntityTag', eid, ctx.getInput<string>('tag'));
  },
};

export const HasTag: NodeBehaviour = {
  id: 'gameobject.hasTag',
  name: 'Has Tag',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Tag',
  description: 'Checks if an entity has a specific tag.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('tag', 'Tag', 'string', ''),
  ],
  outputs: [
    output('hasTag', 'Has Tag', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:hasTag', [ctx.resolveInput('entity'), ctx.resolveInput('tag')], 'bool');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('hasTag', ctx.bridge.call('bridge:hasTag', eid, ctx.getInput<string>('tag')) as boolean ?? false);
  },
};

export const GetParent: NodeBehaviour = {
  id: 'gameobject.getParent',
  name: 'Get Parent',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'ArrowUp',
  description: 'Gets the parent entity.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('parent', 'Parent', 'entity'),
    output('hasParent', 'Has Parent', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getParent', [ctx.resolveInput('entity')], 'entity');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const parent = ctx.bridge.call('bridge:getParent', eid) as number ?? -1;
    ctx.setOutput('parent', parent);
    ctx.setOutput('hasParent', parent !== -1);
  },
};

export const SetParent: NodeBehaviour = {
  id: 'gameobject.setParent',
  name: 'Set Parent',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Link',
  description: 'Sets the parent of an entity.',
  keywords: ['attach', 'child', 'hierarchy'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('parent', 'Parent', 'entity', 0),
    input('keepWorld', 'Keep World Position', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setParent', [ctx.resolveInput('entity'), ctx.resolveInput('parent'), ctx.resolveInput('keepWorld')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setParent', eid, ctx.getInput<number>('parent'), ctx.getInput<boolean>('keepWorld'));
  },
};

export const ClearParent: NodeBehaviour = {
  id: 'gameobject.clearParent',
  name: 'Clear Parent',
  category: 'Events',
  color: '#2ecc71',
  icon: 'Unlink',
  description: 'Removes the parent from an entity.',
  keywords: ['detach', 'unparent', 'orphan'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('keepWorld', 'Keep World Position', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:clearParent', [ctx.resolveInput('entity'), ctx.resolveInput('keepWorld')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:clearParent', eid, ctx.getInput<boolean>('keepWorld'));
  },
};

export const GetChildCount: NodeBehaviour = {
  id: 'gameobject.getChildCount',
  name: 'Get Child Count',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Users',
  description: 'Gets the number of child entities.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('count', 'Count', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getChildCount', [ctx.resolveInput('entity')], 'i32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('count', ctx.bridge.call('bridge:getChildCount', eid) as number ?? 0);
  },
};

export const GetChildAt: NodeBehaviour = {
  id: 'gameobject.getChildAt',
  name: 'Get Child At Index',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'User',
  description: 'Gets a child entity by index.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
    input('index', 'Index', 'i32', 0),
  ],
  outputs: [
    output('child', 'Child', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getChildAt', [ctx.resolveInput('entity'), ctx.resolveInput('index')], 'entity');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('child', ctx.bridge.call('bridge:getChildAt', eid, ctx.getInput<number>('index')) as number ?? -1);
  },
};

export const ExpandEntity: NodeBehaviour = {
  id: 'gameobject.expandEntity',
  name: 'Expand Entity',
  category: 'Events',
  isPure: true,
  color: '#2ecc71',
  icon: 'Expand',
  description: 'Decomposes an entity into its name, transform, and ID.',
  keywords: ['decompose', 'unpack', 'properties', 'info', 'debug', 'inspect'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    // NOTE: no 'entity' output — it would collide with the 'entity' input port id (you already
    // have the entity you passed in). Transform outputs are best-effort in compile (only the
    // primary output resolves); use the dedicated Get Position / Get Scale nodes for those in graphs.
    output('name', 'Name', 'string'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
    output('rotation', 'Rotation', 'f32'),
    output('scaleX', 'Scale X', 'f32'),
    output('scaleY', 'Scale Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getEntityName', [ctx.resolveInput('entity')], 'string');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('name', (ctx.bridge.call('getEntityName', eid) as string) ?? '');
    ctx.setOutput('x', ctx.bridge.getPositionX(eid));
    ctx.setOutput('y', ctx.bridge.getPositionY(eid));
    ctx.setOutput('rotation', ctx.bridge.getRotation(eid));
    ctx.setOutput('scaleX', ctx.bridge.getScaleX(eid));
    ctx.setOutput('scaleY', ctx.bridge.getScaleY(eid));
  },
};

export const GAMEOBJECT_NODES: readonly NodeBehaviour[] = [
  GetSelf, FindByName, FindByTag, DestroyEntity,
  SetActive, IsActive, GetEntityName,
  Instantiate, InstantiateAt,
  GetEntityTag, SetEntityTag, HasTag,
  GetParent, SetParent, ClearParent,
  GetChildCount, GetChildAt,
  ExpandEntity,
];
